import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, Button, Container, Typography } from '@mui/material';
import { Package } from 'lucide-react';
import { FC, memo } from 'react';
import BetaBadge from 'src/components/BetaBadge.component';
import { appBarHeight } from 'src/constants/layout';

import { useLanesState } from './hooks/useLanesState';
import LaneDetailSheet from './presenters/LaneDetailSheet';
import LaneFilters from './presenters/LaneFilters';
import LaneSummaryCard from './presenters/LaneSummaryCard';
import LaneTable from './presenters/LaneTable';

type Props = {
  startDate: string;
  endDate?: string;
};

/**
 * V2ShipmentOrderLanes Presenter
 * レーン管理画面のUI層
 */
const Presenter: FC<Props> = memo(({ startDate, endDate }) => {
  const {
    lanes,
    summary,
    pagination,
    filters,
    selectedLane,
    isDetailOpen,
    isLoading,
    isSummaryLoading,
    isError,
    refetch,
    updateFilters,
    setPage,
    openDetail,
    closeDetail,
    handleDateRangeChange,
  } = useLanesState({
    startDate,
    endDate: endDate || startDate,
  });

  return (
    <>
      <title>レーン管理</title>
      <Box
        sx={{
          bgcolor: 'background.paper',
          height: `calc(100vh - ${appBarHeight}px)`, // appBarHeightを考慮
          display: 'flex',
          flexDirection: 'column',
          p: 1,
          overflow: 'hidden',
        }}
      >
        {/* エラー時のリトライボタン */}
        {isError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={void refetch}
              >
                再試行
              </Button>
            }
          >
            データの取得に失敗しました。再試行してください。
          </Alert>
        )}
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flex: 1,
            mb: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'space-between', alignItems: 'center', }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 1,
                minHeight: '70px',
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    p: 1,
                    bgcolor: 'primary.main',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Package size={20} color="white" />
                </Box>
                <Box>
                  <Typography variant="h3" fontWeight="bold" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                    レーン管理
                    <BetaBadge size="md" />
                  </Typography>
                  <Typography variant="body1">輸送区間ごとに集約して、効率的に車両割当や状況確認を行う画面</Typography>
                </Box>
              </Box>
            </Box>
            <LaneSummaryCard summary={summary} isLoading={isSummaryLoading} />
          </Box>

          {/* フィルタ領域 */}
          <LaneFilters
            filters={filters}
            onFiltersChange={updateFilters}
            onDateRangeChange={handleDateRangeChange}
          />

          {/* レーンテーブル */}
          <LaneTable
            lanes={lanes}
            isLoading={isLoading}
            pagination={pagination}
            pageSize={filters.per}
            onRowClick={openDetail}
            onPageChange={setPage}
          />

          {/* 詳細シート */}
          <LaneDetailSheet
            selectedLane={selectedLane}
            startOn={startDate}
            isOpen={isDetailOpen}
            onClose={closeDetail}
          />
        </Container>
      </Box>
    </>
  );
});

export default Presenter;
